import api, { streamFetch } from 'api/api.config';

const getBasePath = (oktaUserId: string): string =>
  `/${oktaUserId}/conversations`;

const getClientIdHeaders = (
  clientId: string | number | undefined,
): Record<string, string> => (clientId ? { 'X-Client-Id': String(clientId) } : {});

export interface AiBaseArgs {
  oktaUserId: string;
  clientId?: string | number;
}

export interface GetConversationsArgs extends AiBaseArgs {
  search?: string;
}

export const getConversations = async ({
  oktaUserId,
  clientId,
  search,
}: GetConversationsArgs): Promise<unknown> => {
  const params = search ? { search: search.trim() } : {};

  const response = await api.get(getBasePath(oktaUserId), {
    headers: getClientIdHeaders(clientId),
    params,
  });

  return response.data;
};

export interface CreateConversationArgs extends AiBaseArgs {
  data: unknown;
}

export const createConversation = async ({
  oktaUserId,
  data,
  clientId,
}: CreateConversationArgs): Promise<unknown> => {
  const response = await api.post(
    getBasePath(oktaUserId),
    JSON.stringify(data),
    { headers: getClientIdHeaders(clientId) },
  );

  return response.data;
};

export interface ConversationIdArgs extends AiBaseArgs {
  conversationId: string | number;
}

export const deleteConversation = async ({
  oktaUserId,
  conversationId,
  clientId,
}: ConversationIdArgs): Promise<void> => {
  await api.delete(`${getBasePath(oktaUserId)}/${conversationId}`, {
    headers: getClientIdHeaders(clientId),
  });
};

export const getConversationMessages = async ({
  oktaUserId,
  conversationId,
  clientId,
}: ConversationIdArgs): Promise<unknown> => {
  const response = await api.get<{ messages: unknown }>(
    `${getBasePath(oktaUserId)}/${conversationId}/messages`,
    { headers: getClientIdHeaders(clientId) },
  );

  return response.data.messages;
};

export interface CreateConversationMessageArgs extends ConversationIdArgs {
  data: unknown;
  onChunk?: (parsed: unknown, fullContent: string) => void;
}

export const createConversationMessage = async ({
  oktaUserId,
  conversationId,
  data,
  onChunk,
  clientId,
}: CreateConversationMessageArgs): Promise<void> => {
  await streamFetch(`${getBasePath(oktaUserId)}/${conversationId}/messages`, {
    body: data,
    onChunk,
    headers: getClientIdHeaders(clientId),
  });
};

export interface SendFeedbackArgs extends ConversationIdArgs {
  messageId: string | number;
  data: unknown;
}

export const sendFeedback = async ({
  oktaUserId,
  conversationId,
  messageId,
  data,
  clientId,
}: SendFeedbackArgs): Promise<void> => {
  await api.post(
    `${getBasePath(
      oktaUserId,
    )}/${conversationId}/messages/${messageId}/feedback`,
    JSON.stringify(data),
    { headers: getClientIdHeaders(clientId) },
  );
};

export interface RegenerateMessageArgs extends ConversationIdArgs {
  messageId: string | number;
  onChunk?: (parsed: unknown, fullContent: string) => void;
}

export const regenerateMessage = async ({
  oktaUserId,
  conversationId,
  messageId,
  onChunk,
  clientId,
}: RegenerateMessageArgs): Promise<void> => {
  await streamFetch(
    `${getBasePath(oktaUserId)}/${conversationId}/messages/${messageId}`,
    {
      method: 'PUT',
      onChunk,
      headers: getClientIdHeaders(clientId),
    },
  );
};
